/**
 * Remounts its subtree whenever the owner of the screen or what the owner may do changes (batch 8 T12c1, #3033).
 *
 * Drafts, an open dialog, a reviewed document, fields and errors all belong to the signed-in owner (tenant, user, session family,
 * generation) and to the rights it holds. The key is read in the very render that sees the change, so no frame shows another owner's
 * content, and nothing of the old subtree survives to report a late result. Unresolved commands are not state of the subtree: they
 * live in the owner's journal and are found again by their own owner.
 *
 * Every public entry point (the panel and each exported dialog) stands inside one. Boundaries nest without doubling: inside another
 * boundary the inner one adds nothing, because the outer one already remounts everything below it in the same render.
 */

import { Fragment, createContext, useContext, type ReactNode } from 'react';
import { useDispatchCacheGuard } from '@/hooks/useProcurementDispatch';
import { accessKey, useDispatchAccess } from './dispatchSession';

const InsideBoundary = createContext(false);

function OwnedRoot({ children }: { children: ReactNode }) {
  useDispatchCacheGuard();
  const access = useDispatchAccess();
  return (
    <InsideBoundary.Provider value>
      <Fragment key={accessKey(access)}>{children}</Fragment>
    </InsideBoundary.Provider>
  );
}

export function DispatchOwnedBoundary({ children }: { children: ReactNode }) {
  return useContext(InsideBoundary) ? <>{children}</> : <OwnedRoot>{children}</OwnedRoot>;
}
